import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../translations';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiTarget, FiAward, FiTrendingUp, FiZap, FiShield, FiGlobe, FiCpu } = FiIcons;

const About = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: FiCpu,
      title: 'AI-Powered Analysis',
      description: 'Advanced AI institutional-grade analysis with professional trading precision',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: FiTrendingUp,
      title: 'Real-Time Data',
      description: 'Live market data integration with TradingView and institutional feeds',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: FiShield,
      title: 'Risk Management',
      description: 'Advanced position sizing with probability-weighted risk assessment',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: FiGlobe,
      title: 'Multi-Market',
      description: 'Forex, Gold, Indices, and Crypto analysis in one platform',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Active Traders', icon: FiUsers },
    { value: '95%', label: 'Accuracy Rate', icon: FiTarget },
    { value: '24/7', label: 'Market Coverage', icon: FiZap },
    { value: '50+', label: 'Countries', icon: FiGlobe }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-4 w-96 h-96 bg-purple-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-40 -right-4 w-96 h-96 bg-cyan-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <main className="relative pt-8 pb-20">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-pink-400 bg-clip-text text-transparent">
                About Snap Chart AI
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Revolutionizing trading analysis with institutional-grade AI
            </p>
          </motion.div>
        </div>

        {/* Stats Section */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
                  <SafeIcon icon={stat.icon} className="text-4xl text-cyan-400 mx-auto mb-3" />
                  <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Platform Showcase */}
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-6xl mx-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-3xl blur-3xl"></div>
            
            <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 overflow-hidden">
              {/* Mock Terminal Interface */}
              <div className="bg-gray-950 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="bg-gray-900 px-4 py-3 flex items-center gap-2 border-b border-white/10">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 text-center text-sm text-gray-400">Snap Chart AI Trading Terminal</div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 p-6">
                  {/* Market Selector */}
                  <div className="md:col-span-3 grid grid-cols-4 gap-3">
                    {['Gold', 'Forex', 'Indices', 'Crypto'].map((market, i) => (
                      <div key={i} className={`p-4 rounded-xl text-center font-bold ${i === 0 ? 'bg-gradient-to-r from-purple-600 to-cyan-600' : 'bg-gray-800'}`}>
                        {market}
                      </div>
                    ))}
                  </div>

                  {/* Chart Area */}
                  <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-2xl font-bold">XAUUSD</div>
                        <div className="text-green-400 text-sm">+0.31% • $4,075.80</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">24H Volume</div>
                        <div className="text-sm font-bold">$2.4B</div>
                      </div>
                    </div>
                    <div className="h-64 bg-gradient-to-br from-purple-500/5 to-cyan-500/5 rounded-lg flex items-end justify-around p-4">
                      {[65, 80, 45, 90, 70, 85, 60, 95, 75, 88, 92, 78].map((height, i) => (
                        <div key={i} className="w-6 bg-gradient-to-t from-purple-600 to-cyan-600 rounded-t" style={{ height: `${height}%` }}></div>
                      ))}
                    </div>
                  </div>

                  {/* AI Analysis Panel */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 rounded-xl p-4 border border-purple-500/20">
                      <div className="flex items-center gap-2 mb-3">
                        <SafeIcon icon={FiCpu} className="text-purple-400" />
                        <span className="font-bold text-sm">AI Analysis</span>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-300">Support: $4,050</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-300">Resistance: $4,120</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          <span className="text-gray-300">R:R Ratio: 1:3.2</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-4 border border-white/5">
                      <div className="flex items-center gap-2 mb-3">
                        <SafeIcon icon={FiTrendingUp} className="text-cyan-400" />
                        <span className="font-bold text-sm">Market Sentiment</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">Bullish</span>
                            <span className="text-green-400">72%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-green-500 to-emerald-600 w-[72%]"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-4 border border-orange-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <SafeIcon icon={FiShield} className="text-orange-400" />
                        <span className="font-bold text-sm">Risk Level</span>
                      </div>
                      <div className="text-2xl font-bold text-yellow-400">Medium</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <p className="text-gray-400 text-lg">
                  ⚡ <span className="text-white font-semibold">Professional-grade tools at your fingertips</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                Why Choose Snap Chart AI
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Institutional-grade features designed for modern traders
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity`}></div>
                <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 hover:border-white/40 transition-all">
                  <div className={`inline-flex p-4 bg-gradient-to-r ${feature.color} rounded-2xl mb-4`}>
                    <SafeIcon icon={feature.icon} className="text-3xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8">
                <SafeIcon icon={FiTarget} className="text-5xl text-purple-400 mb-6" />
                <h3 className="text-3xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-400 leading-relaxed text-lg">
                  To democratize institutional-grade trading analysis through advanced artificial intelligence, making professional tools accessible to traders worldwide.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8">
                <SafeIcon icon={FiAward} className="text-5xl text-cyan-400 mb-6" />
                <h3 className="text-3xl font-bold mb-4">Our Vision</h3>
                <p className="text-gray-400 leading-relaxed text-lg">
                  Empowering traders with institutional-level insights at their fingertips, transforming how people approach financial markets with AI-powered precision.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default About;